/**
 * Notifications (ARCHITECTURE.md §8).
 *
 * The activity feed: signals, order events, approval requests, AI verdicts, and
 * risk breaches, with read state and per-event-type filtering. Delivery *policy*
 * (which events go to which channels) lives in Settings → General; this page is
 * the read model.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge, cn, formatDateTime } from "@richrich/ui";
import type { Notification, NotificationEventType } from "@richrich/contracts";
import { useI18n } from "@richrich/i18n/react";
import type { MessageKey } from "@richrich/i18n";
import { PageShell } from "../components/PageShell";
import { Button, EmptyState, ErrorNote } from "../components/ui/controls";
import { EVENT_META, notificationsQuery } from "../features/notifications/api";
import { useMarkNotificationsRead } from "../features/notifications/hooks";

/**
 * i18n key per event type. `EVENT_META` (features/notifications/api.ts) carries the
 * badge tone; the human label is resolved here at render so the feed is localized
 * without mutating that shared presentation table.
 */
export const EVENT_LABEL_KEY: Record<NotificationEventType, MessageKey> = {
  signal: "notifications.eventSignal",
  orderPlaced: "notifications.eventOrderPlaced",
  orderFilled: "notifications.eventOrderFilled",
  approvalRequested: "notifications.eventApprovalRequested",
  aiVerdict: "notifications.eventAiVerdict",
  riskBreach: "notifications.eventRiskBreach",
  error: "notifications.eventError",
};

export const Route = createFileRoute("/notifications")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(notificationsQuery);
  },
  component: NotificationsPage,
});

type Filter = "all" | "unread" | NotificationEventType;

function NotificationsPage() {
  const { t } = useI18n();
  const query = useQuery(notificationsQuery);
  const markRead = useMarkNotificationsRead();
  const [filter, setFilter] = useState<Filter>("all");

  const all = useMemo(() => query.data?.items ?? [], [query.data]);
  const unreadCount = all.filter((n) => n.status !== "read").length;

  const shown = all.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return n.status !== "read";
    return n.eventType === filter;
  });

  const presentTypes = useMemo(() => {
    const set = new Set<NotificationEventType>();
    for (const n of all) set.add(n.eventType);
    return [...set];
  }, [all]);

  return (
    <PageShell
      eyebrow={t("notifications.eyebrow")}
      title={t("notifications.title")}
      description={t("notifications.description")}
      loading={query.isLoading}
      actions={
        <Button
          variant="secondary"
          size="sm"
          disabled={unreadCount === 0}
          pending={markRead.isPending}
          onClick={() => markRead.mutate({})}
        >
          {t("notifications.markAllRead")}
          {unreadCount > 0 ? ` (${unreadCount})` : ""}
        </Button>
      }
    >
      {query.isError ? (
        <ErrorNote>{t("notifications.loadError")}</ErrorNote>
      ) : (
        <div className="flex flex-col gap-4">
          <FilterBar
            filter={filter}
            onChange={setFilter}
            unreadCount={unreadCount}
            presentTypes={presentTypes}
          />
          {shown.length === 0 ? (
            <EmptyState
              title={t("notifications.emptyTitle")}
              description={
                filter === "all" ? t("notifications.emptyAll") : t("notifications.emptyFiltered")
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {shown.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onMarkRead={() => markRead.mutate({ ids: [n.id] })}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </PageShell>
  );
}

function FilterBar({
  filter,
  onChange,
  unreadCount,
  presentTypes,
}: {
  filter: Filter;
  onChange: (f: Filter) => void;
  unreadCount: number;
  presentTypes: readonly NotificationEventType[];
}) {
  const { t } = useI18n();
  const chips: { value: Filter; label: string }[] = [
    { value: "all", label: t("notifications.filterAll") },
    {
      value: "unread",
      label: `${t("notifications.filterUnread")}${unreadCount > 0 ? ` (${unreadCount})` : ""}`,
    },
    ...presentTypes.map((type) => ({
      value: type as Filter,
      label: t(EVENT_LABEL_KEY[type]),
    })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.value}
          type="button"
          onClick={() => onChange(chip.value)}
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            filter === chip.value
              ? "border-brand/40 bg-brand/10 text-brand"
              : "border-hairline bg-surface text-muted hover:text-ink",
          )}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function NotificationRow({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: () => void;
}) {
  const { t, locale } = useI18n();
  const meta = EVENT_META[notification.eventType];
  const unread = notification.status !== "read";
  return (
    <li
      className={cn(
        "flex items-start gap-3 rounded-panel border px-4 py-3",
        unread ? "border-brand/30 bg-brand/[0.04]" : "border-hairline bg-surface",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          unread ? "bg-brand" : "bg-transparent",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={meta.tone}>{t(EVENT_LABEL_KEY[notification.eventType])}</Badge>
          <span className="truncate text-sm font-medium text-ink">{notification.title}</span>
          {notification.priority === "high" ? (
            <Badge tone="serious" dot>
              {t("notifications.priorityHigh")}
            </Badge>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-ink-secondary">{notification.body}</p>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted">
          <span>{formatDateTime(notification.ts, locale)}</span>
          <span aria-hidden>·</span>
          <span className="uppercase tracking-wider">{notification.channel}</span>
        </div>
      </div>
      {unread ? (
        <button
          type="button"
          onClick={onMarkRead}
          className="shrink-0 text-xs font-medium text-brand hover:underline"
        >
          {t("notifications.markRead")}
        </button>
      ) : null}
    </li>
  );
}
